import { CircularProgress } from "@mui/material";
import axios from "axios";
import { t } from "i18next";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from "react-toastify";

import DatahubService from "../services/Datahub/DatahubService";
import uniformizeDeliveryType from "../utils/uniformizeDeliveryType";


const CarrierSelectForm = ({ vehicleList, address, carrier, setCarrier, goToNextStep }) => {
    const [deliveryProviderList, setDeliveryProviderList] = useState([]);
    const timer = useRef(null);
    const cancelToken = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    const groupSolutions = useMemo(() => {
        return deliveryProviderList.filter(provider => provider.Plate.includes(';'));
    }, [deliveryProviderList]);

    const groupSolutionsByProvider = useMemo(() => {
        return groupSolutions.reduce((acc, solution) => {
            const transporter = solution.Transporteur;
            if (!acc[transporter]) {
                acc[transporter] = [];
            }
            acc[transporter].push(solution);
            acc[transporter].sort((a, b) => a.PriceInclTax - b.PriceInclTax);
            return acc;
        }, {});
    }, [groupSolutions]);

    const individualSolutions = useMemo(() => {
        return deliveryProviderList.filter(provider => !provider.Plate.includes(';'));
    }, [deliveryProviderList]);

    const individualSolutionsByPlate = useMemo(() => {
        return individualSolutions.reduce((acc, solution) => {
            const plate = solution.Plate;
            if (!acc[plate]) {
                acc[plate] = {};
            }
            const transporter = solution.Transporteur;
            if (!acc[plate][transporter]) {
                acc[plate][transporter] = [];
            }
            acc[plate][transporter].push(solution);
            acc[plate][transporter].sort((a, b) => a.PriceInclTax - b.PriceInclTax);
            return acc;
        }, {});
    }, [individualSolutions]);

    useEffect(() => {
        setIsLoading(true);
        clearTimeout(timer.current);

        timer.current = setTimeout(async () => {
            if (cancelToken.current) {
                cancelToken.current.cancel();
            }
            cancelToken.current = axios.CancelToken.source();


            const data = {
                departureAddress: address.startAddress,
                arrivalAddress: address.endAddress,
                vehicleIdentificationConfirmed: vehicleList,
            };

            DatahubService.getPricing(data, cancelToken.current)
                .then((response) => {
                    if (!response.data.Solutions) {
                        throw new Error();
                    }
                    setDeliveryProviderList(response.data?.Solutions);
                }).catch(() => {
                    toast.warning(t('carrierSelectForm:dataRetrievalFailed'),
                        {
                            position: "top-center",
                            autoClose: 3500,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                        }
                    );
                }).finally(() => {
                    setIsLoading(false);
            });
        }, 100);

        return () => {
            if (cancelToken.current) {
                cancelToken.current.cancel();
            }
        };
    }, []);

    return (
        <form
            style={{
                background: '#e0e0e0',
                padding: '10px 20px',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                minheight: '100px',
            }}
            onSubmit={() => {}}
        >
            {isLoading ?
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <CircularProgress size={24}/>
                </div> :
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                    }}
                >
                    {/* Solutions groupées */}
                    {Object.entries(groupSolutionsByProvider).length > 0 &&
                        <div>
                            <h2>Transport en groupe</h2>
                            <div style={{ marginLeft: "20px" }}>
                                {Object.entries(groupSolutionsByProvider).map(([transporter, solutions]) => (
                                    <div
                                        key={transporter}
                                        style={{ marginLeft: '20px' }}
                                    >
                                        <h4>{transporter}</h4>
                                        {solutions.map((solution) => (
                                            <div style={{ marginLeft: '20px' }}>
                                                <label key={solution.Type} style={{ display: 'block', margin: '5px 0' }}>
                                                    <input
                                                        type="radio"
                                                        name="deliveryProvider"
                                                        value={solution.id}
                                                        style={{ marginRight: '10px' }}
                                                    />
                                                    {`${uniformizeDeliveryType(solution.Type)} - ${Number(solution.PriceInclTax).toFixed(2)} € TTC`}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    }

                    {/* Solutions individuelles */}
                    <div>
                        <h2>Transports individuels</h2>
                        {Object.entries(individualSolutionsByPlate).map(([plate, transporters]) => (
                            <div
                                key={plate}
                                style={{ marginLeft: '20px' }}
                            >
                                <h3>{plate}</h3>
                                {Object.entries(transporters).map(([transporterName, transporterSolutions]) => (
                                    <div
                                        key={transporterName}
                                        style={{ marginLeft: '20px' }}
                                    >
                                        <h4>{transporterName}</h4>
                                        <div style={{ marginLeft: '20px' }}>
                                            {transporterSolutions.map((solution) => (
                                                <label key={`${solution.Plate}-${solution.Type}`} style={{ display: 'block', margin: '5px 0' }}>
                                                    <input
                                                        type="radio"
                                                        name="deliveryProvider"
                                                        value={solution.id}
                                                        style={{ marginRight: '10px' }}
                                                    />
                                                    {`${uniformizeDeliveryType(solution.Type)} - ${Number(solution.PriceInclTax).toFixed(2)} € TTC`}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            }

            {/* CODE POUR LA V3 */}
            {/*<div*/}
            {/*    style={{*/}
            {/*        display: 'flex',*/}
            {/*        flexDirection: 'row',*/}
            {/*        justifyContent: 'end',*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <button*/}
            {/*        className="homepage-button"*/}
            {/*        style={{*/}
            {/*            width: '120px',*/}
            {/*            color: 'white',*/}
            {/*            border: 'none',*/}
            {/*            padding: '10px 20px',*/}
            {/*            borderRadius: '4px',*/}
            {/*        }}*/}
            {/*        type="submit"*/}
            {/*        disabled={!selectedProvider}*/}
            {/*    >*/}
            {/*        {t('common:next')}*/}
            {/*    </button>*/}
            {/*</div>*/}
        </form>
    );
}

export default CarrierSelectForm;
